import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Radio, Space, Typography, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  saveAsInputColumn,
  saveAsOutputColumn,
  getInputColumnsData,
  getOutputColumnsData,
} from "../store/workspaceSlice";
import { X } from "lucide-react";

const { Title } = Typography;

const ViewInputFile = ({ isModalOpen, setIsModalOpen, fileData = [] }) => {
  const dispatch = useDispatch();
  const existingInputColumns = useSelector(getInputColumnsData) || [];
  const existingOutputColumns = useSelector(getOutputColumnsData) || [];

  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [inputColumns, setInputColumns] = useState(new Set());
  const [outputColumns, setOutputColumns] = useState(new Set());
  const [columnDataTypes, setColumnDataTypes] = useState(new Map());
  const [selectedDataType, setSelectedDataType] = useState("2");
  const [importMode, setImportMode] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const originalSnapshotRef = useRef({
    inputColumns: new Set(),
    outputColumns: new Set(),
    columnDataTypes: new Map(),
  });

  // --- Utility: check unsaved changes ---
  const hasUnsavedChanges = () => {
    const {
      inputColumns: origIn,
      outputColumns: origOut,
      columnDataTypes: origTypes,
    } = originalSnapshotRef.current;

    if (
      origIn.size !== inputColumns.size ||
      origOut.size !== outputColumns.size
    )
      return true;

    for (const val of inputColumns) if (!origIn.has(val)) return true;
    for (const val of outputColumns) if (!origOut.has(val)) return true;

    if (origTypes.size !== columnDataTypes.size) return true;
    for (const [key, val] of columnDataTypes) {
      if (origTypes.get(key) !== val) return true;
    }

    return false;
  };

  // --- Safe Modal Close ---
  const handleModalClose = () => {
    if (hasUnsavedChanges()) {
      Modal.confirm({
        title: "Unsaved Changes Detected",
        content:
          "You have unsaved column changes. Are you sure you want to close without saving?",
        okText: "Discard Changes",
        cancelText: "Cancel",
        okType: "danger",
        centered: true,
        onOk: () => {
          const { inputColumns, outputColumns, columnDataTypes } =
            originalSnapshotRef.current;
          setInputColumns(new Set(inputColumns));
          setOutputColumns(new Set(outputColumns));
          setColumnDataTypes(new Map(columnDataTypes));
          setSelectedColumns(new Set());
          setImportMode(false);
          setShowSummary(
            inputColumns.size > 0 || outputColumns.size > 0 ? true : false
          );
          message.info("Unsaved changes discarded");
          setIsModalOpen(false);
        },
      });
    } else {
      setIsModalOpen(false);
    }
  };

  const getColumnIndex = (columnLabel) => {
    if (!columnLabel) return -1;
    let index = 0;
    for (let i = 0; i < columnLabel.length; i++) {
      index = index * 26 + (columnLabel.charCodeAt(i) - 64);
    }
    return index - 1;
  };

  const getColumnLabel = (index) => {
    if (typeof index !== "number" || index < 0) return "";
    let label = "";
    let num = index;
    while (num >= 0) {
      label = String.fromCharCode(65 + (num % 26)) + label;
      num = Math.floor(num / 26) - 1;
    }
    return label;
  };

  const getDataTypeLabel = (dataType) => {
    switch (dataType) {
      case "1":
        return "Text";
      case "2":
        return "Numeric";
      case "3":
        return "Date";
      default:
        return "Not set";
    }
  };

  useEffect(() => {
    if (!isModalOpen) return;

    const columnCount = fileData?.[0]?.length || 0;
    const newInput = new Set();
    const newOutput = new Set();
    const newDataTypes = new Map();

    existingInputColumns.forEach((col) => {
      const idx = getColumnIndex(col.inColChr);
      if (idx >= 0 && idx < columnCount) {
        newInput.add(idx);
        if (col.inColDT) newDataTypes.set(idx, col.inColDT);
      }
    });

    existingOutputColumns.forEach((col) => {
      const idx = getColumnIndex(col.outColChr);
      if (idx >= 0 && idx < columnCount) {
        newOutput.add(idx);
        if (col.outColDT && !newDataTypes.has(idx))
          newDataTypes.set(idx, col.outColDT);
      }
    });

    setInputColumns(newInput);
    setOutputColumns(newOutput);
    setColumnDataTypes(newDataTypes);

    originalSnapshotRef.current = {
      inputColumns: new Set(newInput),
      outputColumns: new Set(newOutput),
      columnDataTypes: new Map(newDataTypes),
    };

    setShowSummary(newInput.size > 0 || newOutput.size > 0);
    setSelectedColumns(new Set());
    setImportMode(false);
    setSelectedDataType("2");
  }, [isModalOpen, fileData, existingInputColumns, existingOutputColumns]);

  const columnCount = useMemo(() => fileData?.[0]?.length || 0, [fileData]);
  const hasColumnsToSave = useMemo(
    () => inputColumns.size > 0 || outputColumns.size > 0,
    [inputColumns, outputColumns]
  );

  const inputSummary = useMemo(() => {
    return Array.from(inputColumns)
      .sort((a, b) => a - b)
      .map((idx) => ({
        column: getColumnLabel(idx),
        columnIndex: idx,
        dataType: getDataTypeLabel(columnDataTypes.get(idx)),
      }));
  }, [inputColumns, columnDataTypes]);

  const outputSummary = useMemo(() => {
    return Array.from(outputColumns)
      .sort((a, b) => a - b)
      .map((idx) => ({
        column: getColumnLabel(idx),
        columnIndex: idx,
        dataType: getDataTypeLabel(columnDataTypes.get(idx)),
      }));
  }, [outputColumns, columnDataTypes]);

  const getColumnHeaderClass = (columnIndex) => {
    const isInput = inputColumns.has(columnIndex);
    const isOutput = outputColumns.has(columnIndex);
    const isSelected = selectedColumns.has(columnIndex);

    if (isSelected) return "bg-purple-400 text-white";
    if (isInput && isOutput) return "bg-blue-400 text-white";
    if (isInput) return "bg-green-400 text-white";
    if (isOutput) return "bg-orange-400 text-white";
    return "";
  };

  const getColumnCellClass = (columnIndex) => {
    const isInput = inputColumns.has(columnIndex);
    const isOutput = outputColumns.has(columnIndex);
    const isSelected = selectedColumns.has(columnIndex);

    if (isSelected) return "bg-purple-100";
    if (isInput && isOutput) return "bg-blue-100";
    if (isInput) return "bg-green-100";
    if (isOutput) return "bg-orange-100";
    return "";
  };

  const handleColumnClick = (columnIndex) => {
    if (!importMode) return;
    setSelectedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnIndex)) next.delete(columnIndex);
      else next.add(columnIndex);
      return next;
    });
  };

  const handleImportAsInput = () => {
    if (selectedColumns.size === 0) {
      message.warning("Select at least one column to import as input.");
      return;
    }
    const newInput = new Set(inputColumns);
    const newTypes = new Map(columnDataTypes);
    selectedColumns.forEach((c) => {
      newInput.add(c);
      newTypes.set(c, selectedDataType);
    });
    setInputColumns(newInput);
    setColumnDataTypes(newTypes);
    setSelectedColumns(new Set());
    if (!showSummary) setShowSummary(true);
    message.success(`${selectedColumns.size} column(s) added as input`);
  };

  const handleImportAsOutput = () => {
    if (selectedColumns.size === 0) {
      message.warning("Select at least one column to import as output.");
      return;
    }
    const newOutput = new Set(outputColumns);
    const newTypes = new Map(columnDataTypes);
    selectedColumns.forEach((c) => {
      newOutput.add(c);
      newTypes.set(c, selectedDataType);
    });
    setOutputColumns(newOutput);
    setColumnDataTypes(newTypes);
    setSelectedColumns(new Set());
    if (!showSummary) setShowSummary(true);

    message.success(`${selectedColumns.size} column(s) added as output`);
  };

  const handleRemoveInputColumn = (columnIndex) => {
    const newInput = new Set(inputColumns);
    newInput.delete(columnIndex);
    setInputColumns(newInput);
    if (!outputColumns.has(columnIndex)) {
      const newTypes = new Map(columnDataTypes);
      newTypes.delete(columnIndex);
      setColumnDataTypes(newTypes);
    }
    message.success(`Removed ${getColumnLabel(columnIndex)} from inputs`);
  };

  const handleRemoveOutputColumn = (columnIndex) => {
    const newOutput = new Set(outputColumns);
    newOutput.delete(columnIndex);
    setOutputColumns(newOutput);
    if (!inputColumns.has(columnIndex)) {
      const newTypes = new Map(columnDataTypes);
      newTypes.delete(columnIndex);
      setColumnDataTypes(newTypes);
    }
    message.success(`Removed ${getColumnLabel(columnIndex)} from outputs`);
  };

  const handleSaveToRedux = () => {
    let inputCount = 0;
    let outputCount = 0;

    inputColumns.forEach((columnIndex) => {
      const columnLabel = getColumnLabel(columnIndex);
      const dataType = columnDataTypes.get(columnIndex) || "2";

      const alreadyExists = existingInputColumns.some(
        (col) => col.inColChr === columnLabel
      );
      if (alreadyExists) return;

      dispatch(
        saveAsInputColumn({
          inColChr: columnLabel,
          inColDT: dataType,
        })
      );
      inputCount++;
    });

    outputColumns.forEach((columnIndex) => {
      const columnLabel = getColumnLabel(columnIndex);
      const dataType = columnDataTypes.get(columnIndex) || "2";

      const alreadyExists = existingOutputColumns.some(
        (col) => col.outColChr === columnLabel
      );
      if (alreadyExists) return;

      dispatch(
        saveAsOutputColumn({
          outColChr: columnLabel,
          outColDT: dataType,
        })
      );
      outputCount++;
    });

    if (inputCount > 0 || outputCount > 0) {
      message.success(
        `Saved ${inputCount} input(s) and ${outputCount} output(s)`
      );
      originalSnapshotRef.current = {
        inputColumns: new Set(inputColumns),
        outputColumns: new Set(outputColumns),
        columnDataTypes: new Map(columnDataTypes),
      };
      // mark as freshly saved so next changes are detected
      setTimeout(() => {
        originalSnapshotRef.current = {
          inputColumns: new Set(inputColumns),
          outputColumns: new Set(outputColumns),
          columnDataTypes: new Map(columnDataTypes),
        };
      }, 0);

      setImportMode(false);
      setSelectedColumns(new Set());
      setShowSummary(true);
    } else {
      message.info("No new columns to save");
    }
  };

  const SummaryPanel = () => (
    <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
      <Title level={5}>Column Summary</Title>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="font-semibold text-green-700 mb-2">
            Input Columns ({inputColumns.size})
          </div>
          {Array.from(inputSummary).length > 0 ? (
            <div className="space-y-1">
              {inputSummary.map((item) => {
                const isInRedux = existingInputColumns.some(
                  (col) => col.inColChr === item.column
                );
                return (
                  <div
                    key={item.column}
                    className="text-sm bg-white p-2 rounded border border-green-200 flex justify-between items-center"
                  >
                    <div>
                      <span className="font-medium">{item.column}</span> -{" "}
                      <span className="text-gray-600">{item.dataType}</span>
                      {isInRedux && (
                        <span className="ml-2 text-gray-400 text-xs">
                          (Saved)
                        </span>
                      )}
                    </div>
                    {!isInRedux && (
                      <button
                        onClick={() =>
                          handleRemoveInputColumn(item.columnIndex)
                        }
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 transition-colors"
                        title="Remove column"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic">No input columns</div>
          )}
        </div>

        <div>
          <div className="font-semibold text-orange-700 mb-2">
            Output Columns ({outputColumns.size})
          </div>
          {Array.from(outputSummary).length > 0 ? (
            <div className="space-y-1">
              {outputSummary.map((item) => {
                const isInRedux = existingOutputColumns.some(
                  (col) => col.outColChr === item.column
                );
                return (
                  <div
                    key={item.column}
                    className="text-sm bg-white p-2 rounded border border-orange-200 flex justify-between items-center"
                  >
                    <div>
                      <span className="font-medium">{item.column}</span> -{" "}
                      <span className="text-gray-600">{item.dataType}</span>
                      {isInRedux && (
                        <span className="ml-2 text-gray-400 text-xs">
                          (Saved)
                        </span>
                      )}
                    </div>
                    {!isInRedux && (
                      <button
                        onClick={() =>
                          handleRemoveOutputColumn(item.columnIndex)
                        }
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 transition-colors"
                        title="Remove column"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic">
              No output columns
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const TableView = () => (
    <div className="overflow-auto max-h-96">
      <table className="min-w-full border-collapse border border-gray-200 text-sm text-left">
        <thead>
          <tr className="bg-gray-100">
            {Array.from({ length: columnCount }).map((_, columnIndex) => (
              <th
                key={columnIndex}
                onClick={() => handleColumnClick(columnIndex)}
                className={`border border-gray-300 px-4 py-2 text-center font-bold ${
                  importMode ? "cursor-pointer hover:bg-blue-200" : ""
                } transition-colors ${
                  importMode ? getColumnHeaderClass(columnIndex) : ""
                }`}
              >
                <div>{getColumnLabel(columnIndex)}</div>
                {importMode && columnDataTypes.has(columnIndex) && (
                  <div className="text-xs font-normal mt-1">
                    ({getDataTypeLabel(columnDataTypes.get(columnIndex))})
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fileData.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"}
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`border border-gray-300 px-4 py-2 text-gray-700 ${
                    importMode ? getColumnCellClass(cellIndex) : ""
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Modal
      footer={null}
      width={1000}
      title="Import Columns from File"
      open={isModalOpen}
      onCancel={handleModalClose}
      maskClosable={false}
      centered
    >
      {fileData?.length > 0 ? (
        <>
          <div className="mb-3 flex justify-between items-center">
            <Space>
              <Button
                type={importMode ? "default" : "primary"}
                onClick={() => {
                  setImportMode((prev) => {
                    const next = !prev;
                    if (prev) setSelectedColumns(new Set());
                    return next;
                  });
                }}
              >
                {importMode ? "Exit Import Mode" : "Import Columns"}
              </Button>

              {hasColumnsToSave && (
                <>
                  <Button onClick={() => setShowSummary((s) => !s)}>
                    {showSummary ? "Hide Summary" : "Show Summary"}
                  </Button>
                  <Button type="primary" onClick={handleSaveToRedux}>
                    Save Imports
                  </Button>
                </>
              )}
            </Space>

            {importMode && (
              <Space>
                <Button
                  type="primary"
                  onClick={handleImportAsInput}
                  disabled={selectedColumns.size === 0}
                >
                  Import as Input ({inputColumns.size})
                </Button>
                <Button
                  type="primary"
                  onClick={handleImportAsOutput}
                  disabled={selectedColumns.size === 0}
                >
                  Import as Output ({outputColumns.size})
                </Button>
              </Space>
            )}
          </div>

          {importMode && (
            <div className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
              <div className="mb-2 font-semibold text-gray-700">
                Select Data Type:
              </div>
              <Radio.Group
                onChange={(e) => setSelectedDataType(e.target.value)}
                value={selectedDataType}
              >
                <Radio value="2">Numeric</Radio>
                <Radio value="1">Text</Radio>
                <Radio value="3">Date</Radio>
              </Radio.Group>
            </div>
          )}

          {showSummary && hasColumnsToSave && <SummaryPanel />}

          <TableView />
        </>
      ) : (
        <div className="text-red-500">No data to be displayed</div>
      )}
    </Modal>
  );
};

export default ViewInputFile;
